import { Badge, IconButton, Tooltip, useMediaQuery } from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import LogoutIcon from "@material-ui/icons/ExitToAppOutlined";
import NotificationsIcon from "@material-ui/icons/Notifications";
import UserProfileIcon from "@material-ui/icons/VerifiedUserRounded";
import { LoadingIndicator } from "ra-ui-materialui";
import React, { forwardRef } from "react";
import {
	AppBar,
	MenuItemLink,
	useNotify,
	usePermissions,
	useQueryWithStore,
	useRedirect,
	UserMenu,
	useTranslate,
} from "react-admin";
import authProvider, { IMPERSONATE_LOGOUT } from "./authProvider";
import { UserIcon, UserProfileApprovalIcon } from "./icons";
import Logo from "./Logo";
import useImpersonate from "./useImpersonate";

const useStyles = makeStyles((theme) => ({
	title: {
		flex: 1,
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		overflow: "hidden",
	},
	spacer: {
		flex: 1,
	},
	badge: {
		color: grey[100],
	},
	badgeCount: {
		color: grey[100],
		right: theme.breakpoints.down("md") ? 5 : -3,
		top: theme.breakpoints.down("md") ? 0 : 5,
	},
	[theme.breakpoints.down("md")]: {
		root: {
			"& .MuiToolbar-root": {
				paddingRight: 0,
			},
			"& .MuiIconButton-root": {
				padding: 0,
				paddingRight: theme.spacing(2),
			},
		},
	},
}));

const NotificationMenu = forwardRef((props, ref) => {
	const redirect = useRedirect();
	const classes = useStyles();
	const translate = useTranslate();
	const { data: notifications, loading } = useQueryWithStore({
		type: "getOne",
		resource: "notifications/status",
		payload: {
			id: "unreaded",
		},
	});
	if (loading) {
		return <LoadingIndicator />;
	}
	return [
		<Tooltip key={1} title={translate("resources.notifications.name", 0)}>
			<IconButton
				color="inherit"
				onClick={() => redirect("/notifications")}
				className={classes.notification}
			>
				<Badge
					badgeContent={notifications && notifications.unreaded}
					color="error"
					className={
						notifications && notifications.unreaded > 0
							? classes.badgeCount
							: classes.badge
					}
				>
					<NotificationsIcon />
				</Badge>
			</IconButton>
		</Tooltip>,
	];
});

const ConfigurationMenu = forwardRef((props, ref) => {
	const { permissions } = usePermissions();
	const translate = useTranslate();
	const redirect = useRedirect();
	const notify = useNotify();
	const impersonate = useImpersonate();
	const handleImpersonateLogout = (e) => {
		e.preventDefault();
		e.stopPropagation();
		authProvider(IMPERSONATE_LOGOUT)
			.then(() => {
				redirect("/");
				setTimeout(() => document.location.reload(), 100);
			})
			.catch((error) => notify(error, "warning"));
	};
	return [
		<MenuItemLink
			key={1}
			ref={ref}
			to="/profile"
			primaryText={translate("ra.auth.profile")}
			leftIcon={<UserProfileIcon />}
			onClick={props.onClick}
		/>,
		permissions && (permissions("admin") || permissions("affiliate_manager")) && (
			<MenuItemLink
				key={2}
				ref={ref}
				to="/users"
				primaryText={translate("menu.items.users")}
				leftIcon={<UserIcon />}
				onClick={props.onClick}
			/>
		),
		permissions && (permissions("admin") || permissions("affiliate_manager")) && (
			<MenuItemLink
				key={3}
				ref={ref}
				to="/user-profile-approvals"
				primaryText={translate("menu.items.user-profile-approvals")}
				leftIcon={<UserProfileApprovalIcon />}
				onClick={props.onClick}
			/>
		),
		impersonate && (
			<MenuItemLink
				key={4}
				ref={ref}
				to="/impersonate"
				leftIcon={<LogoutIcon />}
				primaryText={translate("ra.auth.impersonate_logout")}
				onClick={handleImpersonateLogout}
			/>
		),
	];
});

const CustomUserMenu = (props) => (
	<UserMenu {...props}>
		<ConfigurationMenu />
	</UserMenu>
);

const CustomAppBar = (props) => {
	const classes = useStyles();
	const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
	return (
		<AppBar {...props} className={classes.root} userMenu={<CustomUserMenu />}>
			{isDesktop && (
				<Typography
					variant="h6"
					color="inherit"
					className={classes.title}
					id="react-admin-title"
				/>
			)}
			<Logo dark={true} />
			<span className={classes.spacer} />
			<NotificationMenu />
		</AppBar>
	);
};

export default CustomAppBar;
